// Phase 30 — Required Rent reverse-yield card.
//
// Sits on the Scenarios tab below the existing price-side reverse calculator.
// Given a fixed purchase price + financing + expenses, solves for the
// monthly rent needed to hit a target return (CoC or cap rate).
//
// Calls:
//   POST /api/deals/{id}/reverse-rent/preset   on mount → 6/7/8/10/12% chips
//   POST /api/deals/{id}/reverse-rent          on chip click / custom recompute
//
// Each result row gets a colored verdict pill (green/amber/red) backed by the
// server's verdict_message_{en|es}. Below the table, a plain-English summary
// line ties it all together (in the user's current language).

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  CheckCircle2, AlertTriangle, XCircle, Loader2, Sparkles, Coins, PercentCircle, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

const DEFAULT_TARGETS = [6, 7, 8, 10, 12];

const VERDICT_TONE = {
  feasible_reasonable: {
    cls: "bg-emerald-100 text-emerald-800 border-emerald-300",
    Icon: CheckCircle2,
    label_en: "Feasible",
    label_es: "Factible",
  },
  feasible_out_of_market: {
    cls: "bg-amber-100 text-amber-800 border-amber-300",
    Icon: AlertTriangle,
    label_en: "Out of market",
    label_es: "Fuera de mercado",
  },
  not_feasible: {
    cls: "bg-rose-100 text-rose-800 border-rose-300",
    Icon: XCircle,
    label_en: "Not feasible",
    label_es: "No factible",
  },
};

function fmtCurrency(n) {
  if (n === null || n === undefined) return "—";
  return Number(n).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function fmtPct(n, digits = 2) {
  if (n === null || n === undefined) return "—";
  return `${Number(n).toFixed(digits)}%`;
}

export default function ReverseRentCard({ deal }) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const isSpanish = (i18n.language || "en").toLowerCase().startsWith("es");

  const STR = useMemo(() => ({
    title: isSpanish ? "Calculadora inversa de renta requerida" : "Required rent reverse calculator",
    subtitle: isSpanish
      ? "A precio actual, ¿qué renta necesitas para alcanzar un retorno objetivo?"
      : "At today's price, what rent do you need to hit a target return?",
    kindCoc: isSpanish ? "CoC %" : "CoC %",
    kindCap: isSpanish ? "Cap %" : "Cap %",
    chipsLabel: isSpanish ? "Objetivos comunes" : "Common targets",
    customLabel: isSpanish ? "% personalizado" : "Custom %",
    recompute: isSpanish ? "Calcular" : "Recompute",
    th_target: isSpanish ? "Objetivo" : "Target",
    th_rent: isSpanish ? "Renta requerida/mes" : "Required rent/mo",
    th_cf: isSpanish ? "Flujo mensual" : "Monthly cash flow",
    th_achieved: isSpanish ? "Logrado" : "Achieved",
    th_verdict: isSpanish ? "Veredicto" : "Verdict",
    market: isSpanish ? "Renta de mercado" : "Market rent",
    current: isSpanish ? "Renta actual" : "Current rent",
    noMarket: isSpanish ? "(sin comparable de mercado)" : "(no market comp)",
    useThisRent: isSpanish ? "Usar esta renta" : "Use this rent",
    brrrrNote: isSpanish
      ? "Nota: BRRRR refi cambia el efectivo total invertido. Esta calculadora usa la posición de compra base."
      : "Note: BRRRR refi changes total cash invested. This calculator uses the base purchase position.",
    loadingPreset: isSpanish ? "Calculando objetivos comunes…" : "Computing common targets…",
    addedToCustom: isSpanish ? "Agregado a la fila personalizada" : "Added to custom row",
  }), [isSpanish]);

  const [kind, setKind] = useState("coc"); // 'coc' | 'cap'
  const [customPct, setCustomPct] = useState("");
  const [loading, setLoading] = useState(true);
  const [computing, setComputing] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // Initial fetch — preset (CoC 6/7/8/10/12)
  const loadPreset = useCallback(async () => {
    if (!deal?.id) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post(`/deals/${deal.id}/reverse-rent/preset`);
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed");
    } finally {
      setLoading(false);
    }
  }, [deal?.id]);

  useEffect(() => { loadPreset(); }, [loadPreset]);

  // Run custom + the 5 preset chips together so the table shows the full row set.
  const runWithTargets = useCallback(async (extraTargets = []) => {
    if (!deal?.id) return;
    setComputing(true);
    try {
      const targets = DEFAULT_TARGETS.map((v) => ({ kind, value_pct: v }));
      for (const t of extraTargets) targets.push(t);
      const { data } = await api.post(`/deals/${deal.id}/reverse-rent`, { targets });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Failed");
    } finally {
      setComputing(false);
    }
  }, [deal?.id, kind]);

  // Re-run when kind toggles (so the entire table reflects CoC or Cap).
  useEffect(() => {
    if (!data) return; // initial preset already populated the table
    runWithTargets([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  const handleCustom = () => {
    const v = parseFloat(customPct);
    if (!Number.isFinite(v) || v <= 0 || v >= 100) {
      toast.error(isSpanish ? "Ingresa un % entre 0 y 100" : "Enter a % between 0 and 100");
      return;
    }
    runWithTargets([{ kind, value_pct: v }]);
  };

  const handleUseRent = (rent) => {
    if (!rent || rent <= 0) return;
    navigate(`/deals/${deal.id}/edit?rent_prefill=${Math.round(rent)}`);
  };

  const rows = data?.results || [];

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="reverse-rent-card">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-indigo-600" />
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700">{STR.title}</h3>
          </div>
          <p className="mt-1 text-sm text-slate-500">{STR.subtitle}</p>
        </div>
        <div className="flex items-center gap-1 rounded-full bg-slate-100 p-1 text-xs">
          <button
            onClick={() => setKind("coc")}
            className={`flex items-center gap-1 rounded-full px-3 py-1 font-medium transition-colors ${
              kind === "coc" ? "bg-white text-indigo-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
            data-testid="reverse-rent-kind-coc"
          >
            <Coins className="h-3 w-3" /> {STR.kindCoc}
          </button>
          <button
            onClick={() => setKind("cap")}
            className={`flex items-center gap-1 rounded-full px-3 py-1 font-medium transition-colors ${
              kind === "cap" ? "bg-white text-indigo-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
            data-testid="reverse-rent-kind-cap"
          >
            <PercentCircle className="h-3 w-3" /> {STR.kindCap}
          </button>
        </div>
      </div>

      {/* Market / current rent reference strip */}
      {data && (
        <div className="mb-4 flex flex-wrap items-center gap-x-5 gap-y-1 rounded-lg bg-slate-50 px-3 py-2 text-xs">
          <span className="text-slate-500">
            <strong className="text-slate-700">{STR.current}:</strong> {fmtCurrency(data.current_monthly_rent)}/mo
          </span>
          <span className="text-slate-500">
            <strong className="text-slate-700">{STR.market}:</strong>{" "}
            {data.market_rent_estimate ? `${fmtCurrency(data.market_rent_estimate)}/mo` : STR.noMarket}
          </span>
        </div>
      )}

      {/* Chip row */}
      <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="reverse-rent-chips">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{STR.chipsLabel}:</span>
        {DEFAULT_TARGETS.map((v) => (
          <button
            key={v}
            onClick={() => runWithTargets([])}
            disabled={computing}
            className="rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-bold text-indigo-700 transition-colors hover:bg-indigo-100 disabled:opacity-50"
            data-testid={`reverse-rent-chip-${v}`}
          >
            {v}%
          </button>
        ))}
        <div className="ms-auto flex items-center gap-2">
          <Label htmlFor="reverse-rent-custom" className="text-xs text-slate-500">
            {STR.customLabel}
          </Label>
          <Input
            id="reverse-rent-custom"
            type="number"
            min="0.1"
            max="99"
            step="0.5"
            value={customPct}
            onChange={(e) => setCustomPct(e.target.value)}
            className="h-7 w-20 text-sm"
            placeholder="9.5"
            data-testid="reverse-rent-custom-input"
          />
          <Button
            size="sm"
            onClick={handleCustom}
            disabled={!customPct || computing}
            data-testid="reverse-rent-recompute-btn"
            className="h-7 bg-indigo-600 px-3 text-xs hover:bg-indigo-700"
          >
            {computing ? <Loader2 className="me-1 h-3 w-3 animate-spin" /> : null}
            {STR.recompute}
          </Button>
        </div>
      </div>

      {/* Result table */}
      {loading ? (
        <div className="flex items-center gap-2 p-6 text-sm text-slate-500" data-testid="reverse-rent-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> {STR.loadingPreset}
        </div>
      ) : error ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800" data-testid="reverse-rent-error">
          {String(error)}
        </div>
      ) : rows.length === 0 ? (
        <div className="p-6 text-sm text-slate-400">—</div>
      ) : (
        <div className="overflow-x-auto" data-testid="reverse-rent-table">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
                <th className="py-2 pr-3">{STR.th_target}</th>
                <th className="py-2 pr-3">{STR.th_rent}</th>
                <th className="py-2 pr-3">{STR.th_cf}</th>
                <th className="py-2 pr-3">{STR.th_achieved}</th>
                <th className="py-2 pr-3">{STR.th_verdict}</th>
                <th className="py-2 pr-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const tone = VERDICT_TONE[r.verdict] || VERDICT_TONE.feasible_reasonable;
                const Icon = tone.Icon;
                const msg = isSpanish ? r.verdict_message_es : r.verdict_message_en;
                const label = isSpanish ? tone.label_es : tone.label_en;
                const rentClass = r.verdict === "feasible_reasonable"
                  ? "bg-emerald-50 font-semibold text-emerald-900"
                  : r.verdict === "feasible_out_of_market"
                    ? "bg-amber-50 font-semibold text-amber-900"
                    : "bg-rose-50 font-semibold text-rose-900";
                return (
                  <tr key={`${r.kind}-${r.target_pct}-${i}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60" data-testid={`reverse-rent-row-${r.target_pct}`}>
                    <td className="py-2 pr-3 font-semibold text-slate-700">{fmtPct(r.target_pct, 1)}</td>
                    <td className={`py-2 pr-3 ${r.feasible ? rentClass : "text-slate-400 line-through"}`}>
                      {r.feasible ? fmtCurrency(r.required_monthly_rent) : "—"}
                    </td>
                    <td className="py-2 pr-3 text-slate-700">
                      {r.feasible ? fmtCurrency(r.monthly_cash_flow) : "—"}
                    </td>
                    <td className="py-2 pr-3 text-slate-700">
                      {r.feasible ? fmtPct(r.achieved_pct, 2) : "—"}
                    </td>
                    <td className="py-2 pr-3">
                      <div className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-bold ${tone.cls}`} data-testid={`reverse-rent-verdict-${r.target_pct}`}>
                        <Icon className="h-3 w-3" />
                        {label}
                      </div>
                      <p className="mt-1 max-w-md text-[11px] leading-snug text-slate-500" data-testid={`reverse-rent-msg-${r.target_pct}`}>
                        {msg}
                      </p>
                    </td>
                    <td className="py-2 pr-3">
                      {r.feasible && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-[11px] text-indigo-600 hover:bg-indigo-50"
                          onClick={() => handleUseRent(r.required_monthly_rent)}
                          data-testid={`reverse-rent-use-${r.target_pct}`}
                        >
                          {STR.useThisRent} <ArrowRight className="ms-1 h-3 w-3" />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Plain-English summary */}
      {data?.summary?.total > 0 && (
        <div
          className={`mt-4 rounded-xl border px-4 py-3 text-sm ${
            data.summary.feasible_count === data.summary.total
              ? "border-emerald-200 bg-emerald-50 text-emerald-900"
              : data.summary.feasible_count === 0
                ? "border-rose-200 bg-rose-50 text-rose-900"
                : "border-indigo-200 bg-indigo-50 text-indigo-900"
          }`}
          data-testid="reverse-rent-summary"
        >
          <strong className="me-1 font-bold">
            {isSpanish ? "Decisión:" : "Decision:"}
          </strong>
          {isSpanish ? data.summary.message_es : data.summary.message_en}
        </div>
      )}

      {/* BRRRR limitation note */}
      {deal?.deal_type === "brrrr" && (
        <p className="mt-3 text-[11px] italic text-slate-400">{STR.brrrrNote}</p>
      )}
    </div>
  );
}
